import { type FC, type HTMLAttributes, useCallback, useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import type { Options } from "react-select";
import { type InjectedFormProps, SubmissionError, formValueSelector, reduxForm } from "redux-form";

import {
  AlertDialog,
  Dialog,
  HeaderDivider,
  InfoToggletip,
  TriggerButton,
  TriggerButtonIntent,
  useDialog,
  useMultilineText,
} from "widgets";
import { usePrevious } from "widgets/hooks";

import styleLayout from "../core/styles/PageLayout.module.scss";

import ButtonHeader from "../core/ButtonHeader.js";
import DataTableContainer from "../core/grid/DataTableContainer.js";
import { ContextWidgetType, type INavigationContextProps } from "../core/navigation-panel/ContextWidget.js";
import type { INavPanelCategory } from "../core/navigation-panel/NavigationPanel.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import PageLayout from "../core/navigation-panel/PageLayout.js";
import { PopoverController } from "../core/overlays/components/OverlayControllers.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";
import TextareaDropzone, { TextareaDropzoneReduxFormField } from "../core/redux-form/TextareaDropzone.js";
import type { IBlastInputFormProps } from "../playbooks/blast/BlastInputForm.js";
import type { IUploadFileResponse } from "@/api/WebApiClient.ts";
import { getFormError, simpleDropdownFieldValidation, simpleFieldValidation } from "@/app-utils/form.ts";
import { useURLViewID } from "@/app-utils/hooks/useUrl.ts";
import { cloneSelectionModel, emptySelectionModel } from "@/app-utils/selection-model.ts";
import { pluralizeWord } from "@/app-utils/string.ts";
import type { Dictionary } from "@/app-utils/type.ts";
import type {
  InputSequenceUploadInput,
  InputTableQuerySpec,
  SequenceType,
  User,
  UserGroup,
  ValidationError,
} from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import { checkCapability } from "@/modules/users/utils/user.js";
import { FILE_VALIDATION_WAITING } from "@/redux/action-types.ts";
import {
  type IFileUpload,
  abortFileUpload,
  anyFileHasStatus,
  getFileServerPath,
  getFilesToBeValidated,
  invalidateAllFiles,
  pendingUploadOrValidation,
  resetAllFileUploads,
  uploadFile,
} from "@/redux/modules/file-upload.ts";
import { loadAccessibleUserGroups } from "@/redux/modules/listing.ts";
import { checkGenomeName } from "@/redux/modules/ncbi-admin.ts";
import type { SavedViewState } from "@/redux/modules/saved-view.ts";
import {
  deleteUploadedSequences,
  submitSequenceUpload,
  validateSequenceFilesForUpload,
} from "@/redux/modules/sequences.ts";
import {
  type ITableData,
  createUniqueKeyFromTableSpec,
  getTableDataSlice,
  triggerTableRefresh,
} from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import {
  CAPABILITY,
  ENTITY_TYPE,
  FILE_STATUS,
  LICENSE_MODULE,
  MAX_LENGTH,
  SEQUENCE_TYPE,
  VIEW,
} from "@/settings/settings.ts";
import { FieldTitle } from "@/ui-modules/playbooks/FieldTitle.tsx";

const VECTORS_MENU_ITEMS: INavPanelCategory[] = [
  {
    label: "Vectors",
    tooltip: "Show the uploaded vector sequences",
    licenseModule: LICENSE_MODULE.EXPLORER,
    navItems: [
      {
        label: "Overview",
        route: "/",
        pageComponent: <ContentBrowserVectorsTablePage />,
      },
    ],
  },
];

export default function VectorsPage() {
  const viewId = useURLViewID();
  const { view, isLoading: isSavedViewLoading } = useSelector<IAppState, SavedViewState>((state) => state.savedView);

  const contextProps: INavigationContextProps = useMemo(() => {
    if (viewId && !isSavedViewLoading && Number.parseInt(view?.id) === viewId) {
      return {
        type: ContextWidgetType.SAVED_VIEW,
        data: view,
      };
    }
    return null;
  }, [viewId, view, isSavedViewLoading]);

  return <PageLayout navPanelCategories={VECTORS_MENU_ITEMS} navPanelCollapsed contextWidgetProps={contextProps} />;
}

const sequencesTableSpec: InputTableQuerySpec = {
  id: -1,
  resultType: ENTITY_TYPE.GENERIC_SEQ,
};

const sequencesTableInitOptions = {
  sortExpression: "-entered",
};

function ContentBrowserVectorsTablePage() {
  const tableData: ITableData = useSelector<IAppState, ITableData>(
    (state) => getTableDataSlice(state.tableData, sequencesTableSpec),
    shallowEqual,
  );
  const viewId = useURLViewID();

  const user: User = useSelector<IAppState, User>((state) => state.auth.session);

  // create count label
  const getCount = (): string => {
    if (tableData?.totalRowCount) {
      if (tableData.filteredRowCount === tableData.totalRowCount) {
        return String(tableData.totalRowCount);
      } else if (tableData.filteredRowCount < tableData.totalRowCount) {
        return `${tableData.filteredRowCount} of ${tableData.totalRowCount}`;
      }
    }
    return null;
  };

  return (
    <>
      <PageHeader
        title={"Vectors"}
        setDocumentTitle
        badge={getCount()}
        actionWidgets={[
          ...(checkCapability(user?.capabilities, CAPABILITY.SEQUENCE_UPLOAD)
            ? [<SequenceUploadButton key={"sequences-page-upload-buttons"} />]
            : []),
          <HeaderDivider key={"divider-sequence-page"} />,
          <ButtonHeader
            viewType={VIEW.TABLE_VIEW.type}
            tableSpec={sequencesTableSpec}
            hideBlastBtn={true}
            key={"sequences-page-buttons-1"}
          />,
        ]}
      />
      <div className={styleLayout.page} style={{ backgroundColor: "white" }}>
        <DataTableContainer
          key={createUniqueKeyFromTableSpec(sequencesTableSpec)}
          tableSpec={sequencesTableSpec}
          tableInitializationOptions={sequencesTableInitOptions}
          viewId={viewId}
        />
      </div>
    </>
  );
}

const SequenceUploadButton: FC = () => {
  const { dialogProps, underlayProps, state } = useDialog<HTMLDivElement>();

  return (
    <>
      <TriggerButton onPress={state.open} icon={"cloud_upload"} label={"Import Sequences"} showLabel />
      {state.isOpen && (
        <SequenceFileUploadManager
          key="upload-form-dialog"
          close={state.close}
          dialogProps={dialogProps}
          underlayProps={underlayProps}
        />
      )}
    </>
  );
};

// KEEPING this component and all wiring intact because will probably be resurrected soon
const SequenceDeletionButton: FC<{ isOffscreen: boolean }> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const tableData: ITableData = useSelector<IAppState, ITableData>(
    (state) => getTableDataSlice(state.tableData, sequencesTableSpec),
    shallowEqual,
  );

  const selectedSequenceIds: number[] = tableData?.selectionModel.selectedIds as number[];
  const deletionDisabled: boolean = selectedSequenceIds?.length === 0;

  return (
    <PopoverController
      isOffscreen={props.isOffscreen}
      actionWidget={(controllerProps) => (
        <TriggerButton
          {...controllerProps}
          tooltip={"Delete selected sequences"}
          icon={"delete"}
          isDisabled={deletionDisabled ? "At least one sequence must be selected to perform the deletion." : null}
          intent={TriggerButtonIntent.Danger}
          label={"Delete"}
          showLabel
        />
      )}
    >
      {(controllerProps) => (
        <AlertDialog
          {...controllerProps}
          mainButton={{
            label: "Delete",
            icon: "delete",
            onPress: () => {
              return dispatch(deleteUploadedSequences(selectedSequenceIds)).then(() => {
                dispatch(
                  // clear the selection model after deletion succeeded
                  triggerTableRefresh(sequencesTableSpec, { selectionModel: cloneSelectionModel(emptySelectionModel) }),
                );
                controllerProps.closeFn();
              });
            },
          }}
          aria-label={"Delete sequence"}
        >
          Are you sure you want to delete {selectedSequenceIds.length} selected{" "}
          {pluralizeWord("sequence", selectedSequenceIds.length)}?
        </AlertDialog>
      )}
    </PopoverController>
  );
};

export interface UploadDialogFormProps {
  fileUploads: Dictionary<IFileUpload>;
  dialogProps: HTMLAttributes<HTMLElement>;
  underlayProps: HTMLAttributes<HTMLElement>;
  close: () => void;
}

interface UploadDialogProps extends UploadDialogFormProps, InjectedFormProps<FormData, any> {}

const SEQUENCE_UPLOAD_FORM_NAME = "SEQUENCE_UPLOAD_FORM";
const SEQUENCE_UPLOAD_FORM_KEYS = {
  SEQUENCES: "sequences",
  SEQUENCE_TYPE: "sequence_type",
  GENOME_NAME: "genome_name",
  USER_GROUPS: "user_groups",
};

const UploadDialog: FC<UploadDialogProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const multilineError = useMultilineText(props.error);

  const userGroups = useSelector<IAppState, UserGroup[]>((state) => state.auth.session.userGroups);
  const isLoadingUserGroups = useSelector<IAppState, boolean>((state) => state.listing.isLoadingUserGroups);
  const sequenceTypes = useSelector<IAppState, SequenceType[]>((state) => state.listing.sequenceTypes);

  const orderedGroupTypes: string[] = [SEQUENCE_TYPE.GENOMIC];

  const sequenceTypeOptions = useMemo(() => {
    let options: Options<SequenceType> = [];
    if (sequenceTypes?.length) {
      options = sequenceTypes.filter((s) => orderedGroupTypes.includes(s.groupType));
    }
    return options;
  }, sequenceTypes);

  // init the sequence type to the first option
  useEffect(() => {
    if (sequenceTypeOptions?.length) {
      props.change(SEQUENCE_UPLOAD_FORM_KEYS.SEQUENCE_TYPE, sequenceTypeOptions[0]);
    }
  }, [sequenceTypeOptions]);

  // when user groups are fetched, we init with the Public one
  useEffect(() => {
    if (userGroups?.length) {
      const publicUsergroup = userGroups.find((ug) => ug.name === "Public");
      if (publicUsergroup) {
        props.change(SEQUENCE_UPLOAD_FORM_KEYS.USER_GROUPS, [publicUsergroup]);
      }
    }
  }, [userGroups]);

  const handleSubmitData = useCallback(
    (values, _dispatch, props: UploadDialogProps) => {
      const sequenceUploadInput = createSequenceUploadInput(values, props.fileUploads);
      if (userGroups.length == 1) {
        sequenceUploadInput.userGroupIds = [userGroups[0].id];
      }

      return dispatch(submitSequenceUpload(sequenceUploadInput)).then(
        (_fulfilled) => {
          dispatch(triggerTableRefresh(sequencesTableSpec));
          props.close();
        },
        // do nothing, the original "submitBlastJob" already executed the business logic regarding the success case
        // the following will throw an error that redux-form understands as a submission error, with a general error that will be
        // available from this.props.error
        (rejected) => {
          throw new SubmissionError({ _error: rejected.message });
        },
      );
    },
    [props.fileUploads, props.close, userGroups],
  );

  const { sequenceTypeId } = useSelector<IAppState, any>((state) => {
    const valueSelector = formValueSelector(SEQUENCE_UPLOAD_FORM_NAME);
    const sequenceType: SequenceType = valueSelector(state, SEQUENCE_UPLOAD_FORM_KEYS.SEQUENCE_TYPE);
    return {
      sequenceTypeId: sequenceType?.id,
    };
  });

  useEffect(() => {
    // if the sequence type is modified then we need to validate again all files, because the sequences need to match
    dispatch(invalidateAllFiles());
  }, [sequenceTypeId]);

  const globalFormError = useSelector<IAppState, string>((state) => getFormError(SEQUENCE_UPLOAD_FORM_NAME, state));

  const previousFileUploads = usePrevious(props.fileUploads);
  useEffect(() => {
    if (
      anyFileHasStatus(props.fileUploads, FILE_STATUS.VALIDATION_WAITING) ||
      Object.keys(previousFileUploads || {}).length !== Object.keys(props.fileUploads).length
    ) {
      props.asyncValidate();
    }
  }, [props.fileUploads]);

  // directly after the upload we set the status of the file to "validation waiting" ; if upstream validation failed then this will stay in the status until the previous validation is resolved
  const uploadCallback = useCallback((file: File, formData: FormData) => {
    dispatch(uploadFile(file, formData)).then((response: IUploadFileResponse) => {
      if (response) {
        // only in case of success
        dispatch({ type: FILE_VALIDATION_WAITING, payload: [response] });
      }
    });
  }, []);

  useEffect(() => {
    dispatch(loadAccessibleUserGroups());
  }, []);

  return (
    <Dialog
      title={"Import Sequences"}
      mainButton={{
        label: "Save",
        onPress: props.handleSubmit(handleSubmitData),
        isLoading: !!(props.submitting || pendingUploadOrValidation(props.fileUploads)),
      }}
      errorCallout={{ message: globalFormError || multilineError }}
      closeFn={props.close}
      domProps={props.dialogProps}
      underlayProps={props.underlayProps}
    >
      <div className="form-group">
        <ReactSelectReduxFormField
          name={SEQUENCE_UPLOAD_FORM_KEYS.SEQUENCE_TYPE}
          label={<FieldTitle label={`Sequence type`} mandatory />}
          placeholder="Select the target sequence type"
          component={ReactSelectField}
          horizontalLayout={false}
          getOptionValue={(sequenceType: SequenceType) => sequenceType.id}
          getOptionLabel={(sequenceType: SequenceType) => sequenceType.label} // required for option search function
          formatOption
          options={sequenceTypeOptions}
          noOptionsMessage={() => "No matching types..."}
          isSearchable
          validate={simpleDropdownFieldValidation}
        />

        <TextareaDropzoneReduxFormField
          name={SEQUENCE_UPLOAD_FORM_KEYS.SEQUENCES}
          title={<FieldTitle label={`Sequence(s)`} mandatory />}
          component={TextareaDropzone}
          horizontalLayout={false}
          validate={validateSequenceField}
          handleUploadFile={uploadCallback}
          abortFileupload={(fileName: string) => dispatch(abortFileUpload(fileName))}
          fileUploads={props.fileUploads}
          onlyFiles
        />

        <InputReduxFormField
          componentClass="input"
          name={SEQUENCE_UPLOAD_FORM_KEYS.GENOME_NAME}
          label={
            <FieldTitle
              label={"Genome name"}
              mandatory
              rightElement={
                <InfoToggletip
                  content={
                    "Genome name is used to display the imported sequences with other genomes in the system. The name must be unique relative to other genomes."
                  }
                />
              }
            />
          }
          placeholder={selMessage({ id: "form.input.placeholder.mandatory" })}
          validate={[simpleFieldValidation]}
          maxLength={MAX_LENGTH.GENOME.NAME}
          component={InputField}
        />

        {userGroups && userGroups.length > 1 && (
          <ReactSelectReduxFormField
            name={SEQUENCE_UPLOAD_FORM_KEYS.USER_GROUPS}
            label={<FieldTitle label={"Genome visibility"} mandatory />}
            options={userGroups}
            getOptionValue={(option: UserGroup) => option.id}
            getOptionLabel={(option: UserGroup) => option.name}
            validate={simpleDropdownFieldValidation}
            isLoading={isLoadingUserGroups}
            creatable={false}
            component={ReactSelectField}
            isMulti={true}
          />
        )}
      </div>
    </Dialog>
  );
};

const createSequenceUploadInput = (values, fileUploads: Dictionary<IFileUpload>): InputSequenceUploadInput => {
  const sequenceType: SequenceType = values[SEQUENCE_UPLOAD_FORM_KEYS.SEQUENCE_TYPE];
  const genomeName: string = values[SEQUENCE_UPLOAD_FORM_KEYS.GENOME_NAME];
  const userGroups: UserGroup[] = values[SEQUENCE_UPLOAD_FORM_KEYS.USER_GROUPS];
  const sequencesUploadInput: InputSequenceUploadInput = {
    sequenceType: sequenceType?.id,
    genomeName: genomeName,
    userGroupIds: userGroups?.map((userGroup) => userGroup.id),
    files: getFileServerPath(fileUploads),
  };
  return sequencesUploadInput;
};

const validateSequenceField = (value) => {
  if (!Array.isArray(value) || !value.length) {
    return "A Sequence file is required.";
  }
};

// currently only the sequence field and genome name are validated
const asyncValidate = async (values: any, dispatch: any, props: IBlastInputFormProps) => {
  if (values == null) {
    return;
  }

  // uniqueness check of the genome name
  const genomeName = values[SEQUENCE_UPLOAD_FORM_KEYS.GENOME_NAME];
  if (genomeName) {
    const nameValidationMessage = await dispatch(checkGenomeName(genomeName));
    if (nameValidationMessage) {
      throw {
        [SEQUENCE_UPLOAD_FORM_KEYS.GENOME_NAME]: nameValidationMessage,
      };
    }
  }

  try {
    const filesToBeValidated: IFileUpload[] = getFilesToBeValidated(props.fileUploads);

    // if an upload has failed then there is no need to continue validation
    // Testing Hint: Mimic a failed upload by removing the authorization header in method uploadFile of WebApiClient.ts
    if (anyFileHasStatus(props.fileUploads, FILE_STATUS.UPLOAD_FAILED)) {
      throw "One or more files failed to upload";
    }
    // if a validation has failed then there is no need to continue validation
    if (anyFileHasStatus(props.fileUploads, FILE_STATUS.VALIDATION_FAILED)) {
      throw "One or more files failed the validation";
    }

    // if all files have been validated already then we send a no-error response
    if (filesToBeValidated.length === 0) {
      return;
    }

    const validationErrors: ValidationError[] = await dispatch(
      validateSequenceFilesForUpload(values[SEQUENCE_UPLOAD_FORM_KEYS.SEQUENCE_TYPE], filesToBeValidated),
    );

    // show error callout below dropzone only if there is exaclty one error, e.g. fine contains too many sequences
    // see SEL9-546
    if (validationErrors?.length === 1) {
      throw validationErrors.map((ve: ValidationError, i: number) => <div key={ve.message + i}>{ve.message}</div>);
    }
  } catch (error) {
    throw {
      [SEQUENCE_UPLOAD_FORM_KEYS.SEQUENCES]:
        error instanceof Error || (error.message && typeof error.message === "string")
          ? "Could not validate the input: " + error.message
          : error || "An unexpected error occurred",
    };
  }

  return;
};

const UploadFormDialog = reduxForm({
  form: SEQUENCE_UPLOAD_FORM_NAME,
  asyncValidate: asyncValidate,
  asyncBlurFields: [SEQUENCE_UPLOAD_FORM_KEYS.GENOME_NAME, SEQUENCE_UPLOAD_FORM_KEYS.SEQUENCES], // so that only blurring this field triggers the async validation
  //asyncChangeFields: [],
})(UploadDialog) as any;

const SequenceFileUploadManager: FC<Omit<UploadDialogFormProps, "fileUploads">> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const fileUploads = useSelector<IAppState, Dictionary<IFileUpload>>(
    (state) => state.filesUploadState.filesUploadLookup,
  );

  // state cleanup
  useEffect(() => {
    return () => {
      dispatch(resetAllFileUploads());
    };
  }, []);

  // so that the redux-form async validation has access to fileUploads via the props !
  return <UploadFormDialog {...props} fileUploads={fileUploads} />;
};
//export UploadFormDialog;
